import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Formik } from "formik";
import React from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { patientPostAction } from "../../actions/patientAction";
import { getPatientProfile } from "../../actions/profileAction";
import FormikControl from "../../components/WraapperComponent/FormikControl";
import { states } from "../../constants/states";
import { getUserDetail } from "../../utils/helper";

const PatientInfoEditPopup = ({ closePopup, patientInfo, patientId }) => {
  const dispatch = useDispatch();
  const patientFormFields = useSelector(
    (state) => state.settings.patientFormFields
  );
  const initialValues = {
    firstname: patientInfo && patientInfo.first_name,
    lastname: patientInfo && patientInfo.last_name,
    nickname: patientInfo && patientInfo.patient_nickname,
    email: patientInfo && patientInfo.email,
    address_1: patientInfo && patientInfo.address_1,
    address_2: patientInfo && patientInfo.address_2,
    city: patientInfo && patientInfo.city,
    state: patientInfo && patientInfo.state,
    phone: patientInfo && patientInfo.phone,
    zip: patientInfo && patientInfo.zip,
    addressNickname: patientInfo && patientInfo.address_nickname,
  };

  const validationSchema = Yup.object({
    firstname: Yup.string().nullable().required("This field is required"),
    lastname: Yup.string().nullable().required("This field is required"),
    email: Yup.string()
      .email()
      .when([], {
        is: () =>
          patientFormFields.find((x) => x.fieldName == "Email")?.isRequired ===
          true,
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable(),
    phone: Yup.string()
      .when([], {
        is: () =>
          patientFormFields.find((x) => x.fieldName == "Phone")?.isRequired ===
          true,
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable()
      .min(10, "Phone number must be 10 digits long")
      .max(10, "Phone number must be 10 digits long")
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        "Phone number is not valid"
      ),

    address_1: Yup.string()
      .when([], {
        is: () =>
          patientFormFields.find((x) => x.fieldName == "Address")
            ?.isRequired === true,
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable(),
    address_2: Yup.string().nullable(),
    city: Yup.string()
      .when([], {
        is: () =>
          patientFormFields.find((x) => x.fieldName == "Address")
            ?.isRequired === true,
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable(),
    state: Yup.string()
      .when([], {
        is: () =>
          patientFormFields.find((x) => x.fieldName == "Address")
            ?.isRequired === true,
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable(),
    zip: Yup.string()
      .when([], {
        is: () =>
          patientFormFields.find((x) => x.fieldName == "Address")
            ?.isRequired === true,
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable()
      .max(5)
      .min(5)
      .matches(/^\d{5}$/, "Zip code is not valid"),
    addressNickname: Yup.string().nullable(),
    nickname: Yup.string()
      .when([], {
        is: () =>
          patientFormFields.find((x) => x.fieldName == "Nickname")
            ?.isRequired === true,
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable(),
  });

  const onSubmit = (values) => {
    const data = {
      first_name: values.firstname,
      last_name: values.lastname,
      nick_name: values.nickname,
      address_1: values.address_1,
      address_2: values.address_2,
      city: values.city,
      state: values.state,
      phone: values.phone,
      zip: values.zip,
      address_nickname:
        values.addressNickname === "" ? null : values.addressNickname,
      email: values.email,
      action: "practitionerSavePatient",
      ppc_id: patientId,
      token: getUserDetail()?.token,
    };
    var params = {
      params: data,
    };
    dispatch(
      patientPostAction(params, () => {
        dispatch(getPatientProfile(patientId));
      })
    );
    closePopup();
  };

  return (
    <Modal size="lg" show={true}>
      <Modal.Header>
        <div className="modal-title">
          <h5 className="profile-title">Patient Information</h5>
          <FontAwesomeIcon icon={faTimesCircle} onClick={closePopup} />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div>
          <p className="page-desc">
            Enter Patient Information. (<span className="required">*</span>{" "}
            Required fields)
          </p>
          <div className="form-section">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({ errors, touched, setFieldValue, handleBlur }) => {
                return (
                  <Form>
                    <div className="row">
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* First Name"
                          name="firstname"
                          handleBlur={handleBlur}
                          hasError={errors.firstname}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* Last Name"
                          name="lastname"
                          handleBlur={handleBlur}
                          hasError={errors.lastname && touched.lastname}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label={
                            patientFormFields.find(
                              (x) => x.fieldName == "Nickname"
                            )?.isRequired === true
                              ? "* ID/Nickname"
                              : "ID/Nickname"
                          }
                          name="nickname"
                          handleBlur={handleBlur}
                          hasError={errors.nickname && touched.nickname}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label={
                            patientFormFields.find(
                              (x) => x.fieldName == "Address"
                            )?.isRequired === true
                              ? "* street address"
                              : "street address"
                          }
                          name="address_1"
                          handleBlur={handleBlur}
                          hasError={errors.address_1 && touched.address_1}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="apt/unit #"
                          name="address_2"
                          handleBlur={handleBlur}
                          hasError={errors.address_2 && touched.address_2}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="Address Description"
                          name="addressNickname"
                          handleBlur={handleBlur}
                          hasError={
                            errors.addressNickname && touched.addressNickname
                          }
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label={
                            patientFormFields.find(
                              (x) => x.fieldName == "Address"
                            )?.isRequired === true
                              ? "* City"
                              : "City"
                          }
                          name="city"
                          handleBlur={handleBlur}
                          hasError={errors.city && touched.city}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="dropdown"
                          label={
                            patientFormFields.find(
                              (x) => x.fieldName == "Address"
                            )?.isRequired === true
                              ? "* State"
                              : "state"
                          }
                          name="state"
                          handleBlur={handleBlur}
                          hasError={errors.state && touched.state}
                          options={states}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label={
                            patientFormFields.find(
                              (x) => x.fieldName == "Phone"
                            )?.isRequired === true
                              ? "* Phone"
                              : "Phone"
                          }
                          name="phone"
                          handleBlur={handleBlur}
                          maxLength={10}
                          onPaste={(e) => {
                            const str = e.clipboardData.getData("Text");
                            const newStr = str.replace(/[^0-9]/g, "");
                            if (str !== newStr) {
                              e.preventDefault();
                              setFieldValue("phone", newStr);
                            }
                          }}
                          hasError={errors.phone && touched.phone}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label={
                            patientFormFields.find(
                              (x) => x.fieldName == "Address"
                            )?.isRequired === true
                              ? "* Zip code"
                              : "Zip code"
                          }
                          name="zip"
                          handleBlur={handleBlur}
                          maxLength={5}
                          hasError={errors.zip && touched.zip}
                        />
                      </div>
                      <div className="col-sm-8">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label={
                            patientFormFields.find(
                              (x) => x.fieldName == "Email"
                            )?.isRequired === true
                              ? "* Email"
                              : "Email"
                          }
                          name="email"
                          handleBlur={handleBlur}
                          hasError={errors.email && touched.email}
                        />
                      </div>
                    </div>
                    <div className="submit-section">
                      <button className="btn-orange" type="submit">
                        Save
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
export default PatientInfoEditPopup;
